import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { UnpaidProcedure } from '@/api/payments.api';
import { formatDate, formatMoney, localDay } from '@/lib/format';
import './DuePanel.css';
import { useTranslation } from 'react-i18next';

interface Props {
  rows: UnpaidProcedure[];
  currency: string;
  /** The pill it hangs from: the panel is placed under it, and a click on it is not „outside". */
  anchor: HTMLElement | null;
  onClose: () => void;
}

/** Under the pill, right edges aligned — measured from the pill, not inherited from the header. */
function placeUnder(anchor: HTMLElement | null): CSSProperties {
  if (!anchor) return {};
  const r = anchor.getBoundingClientRect();
  return {
    position: 'fixed',
    top: r.bottom + 8,
    right: Math.max(8, window.innerWidth - r.right),
  };
}

/**
 * Ce e de încasat, la un clic pe pastila din antet.
 *
 * Pastila arăta cifra și te trimitea în Achitări, unde trebuia să cauți din
 * nou. Aici e lista în sine: fiecare lucrare terminată și neachitată, grupată
 * pe pacient, cu suma și cu drumul spre încasare. Recepția vede lucrarea
 * imediat ce medicul i-a pus statutul „Finalizat" — de asta lista se
 * reîmprospătează la fiecare schimbare de status, nu doar la minut.
 *
 * Desenată într-un portal, peste pagină. Agățată în antet, rămânea sub ce își
 * face pe pagină propriul strat — tabelul cu antet lipit, calendarul, taburile
 * fișei — și pe unele ecrane se deschidea în spatele lor.
 */
export function DuePanel({ rows, currency, anchor, onClose }: Props) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const wrap = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>(() => placeUnder(anchor));

  useEffect(() => {
    const place = () => setStyle(placeUnder(anchor));
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The pill toggles it itself; counting it as outside would close the
      // panel on mousedown and open it again on click.
      if (anchor?.contains(target)) return;
      if (!wrap.current?.contains(target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, anchor]);

  /** Un pacient, o intrare: recepția încasează pe om, nu pe procedură. */
  const groups = useMemo(() => {
    const byPatient = new Map<
      string,
      { id: string; name: string; due: number; items: UnpaidProcedure[] }
    >();
    for (const r of rows) {
      const g = byPatient.get(r.patientId) ?? {
        id: r.patientId,
        name: r.patientName,
        due: 0,
        items: [],
      };
      g.due += Number(r.due);
      g.items.push(r);
      byPatient.set(r.patientId, g);
    }
    // Cele mai noi întâi: lucrarea terminată azi e cea pentru care pacientul e
    // încă la ghișeu. În grup, la fel; iar grupurile, după lucrarea lor cea mai
    // nouă — suma decide doar între două din aceeași clipă.
    const when = (i: UnpaidProcedure) => i.finishedAt ?? '';
    for (const g of byPatient.values()) g.items.sort((x, y) => when(y).localeCompare(when(x)));
    return [...byPatient.values()].sort(
      (a, b) => when(b.items[0]!).localeCompare(when(a.items[0]!)) || b.due - a.due,
    );
  }, [rows]);

  const total = groups.reduce((s, g) => s + g.due, 0);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return createPortal(
    <div
      className="due due--float"
      ref={wrap}
      role="dialog"
      aria-label={t('deIncasatAzi')}
      style={style}
    >
      <header>
        <div>
          <strong>{t('deIncasatAzi')}</strong>
          <small>
            {t('pacienti2', { count: groups.length })} ·{' '}
            {rows.length === 1 ? t('oLucrare') : t('lucrari', { count: rows.length })}
          </small>
        </div>
        <span className="due__total tabular">{formatMoney(total, currency)}</span>
        <button type="button" onClick={onClose} aria-label={t('inchide')} title={t('inchide')}>
          <X size={14} />
        </button>
      </header>

      {groups.length === 0 ? (
        <p className="due__empty">{t('nimicDeIncasatAzi')}</p>
      ) : (
        <ul className="due__list">
          {groups.map((g) => (
            <li key={g.id}>
              <button
                type="button"
                className="due__patient"
                onClick={() => go(`/patients/${g.id}?tab=plati`)}
              >
                <span className="due__name">{g.name}</span>
                <span className="due__amount tabular">{formatMoney(g.due, currency)}</span>
              </button>
              <ul className="due__items">
                {g.items.map((i) => (
                  <li key={i.id}>
                    <span className="due__phase">
                      {i.phase}
                      {i.teeth ? ` · ${i.teeth}` : ''}
                    </span>
                    <span className="due__when">{formatDate(localDay(i.finishedAt))}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <footer>
        <button type="button" onClick={() => go('/payments?record=1')}>
          {t('incaseaza')}
        </button>
        <button type="button" className="due__all" onClick={() => go('/payments?tab=debts')}>
          {t('veziToateRestantele')}
        </button>
      </footer>
    </div>,
    document.body,
  );
}
